<template>
  <aside class="ai-images-panel border rounded p-3 bg-gray-50 flex-shrink-0">
    <div class="flex justify-between items-center mb-1">
      <h4 class="text-sm font-bold">
        {{ $t("message.product_images.ai_images_title") }}
        <span v-if="images.length" class="text-gray-400 font-normal">
          ({{ images.length }})
        </span>
      </h4>
      <button
        class="text-gray-400 hover:text-gray-700"
        :title="$t('message.product_images.ai_images_title')"
        @click="load()"
      >
        <i class="mdi mdi-refresh"></i>
      </button>
    </div>
    <p class="text-xs text-gray-500 mb-3">
      {{ $t("message.product_images.ai_images_hint") }}
    </p>

    <div v-if="loading" class="text-center text-gray-400 py-6">
      <i class="mdi mdi-loading mdi-spin text-2xl"></i>
    </div>

    <p
      v-else-if="!images.length"
      class="text-xs text-gray-400 py-6 text-center"
    >
      {{ $t("message.product_images.ai_images_empty") }}
    </p>

    <div v-else class="ai-images-list">
      <div
        v-for="image in images"
        :key="image.id"
        class="border rounded overflow-hidden bg-white"
      >
        <div class="ai-images-thumb" @click="openFull(image)">
          <img :src="image.url" :title="image.prompt" />
          <span
            v-if="image.reviewStatus"
            class="ai-images-badge"
            :title="
              image.reviewScore ? image.reviewScore + '/10' : image.reviewStatus
            "
          >
            <i
              :class="
                image.reviewStatus === 'approved'
                  ? 'mdi mdi-check-circle text-green-600'
                  : 'mdi mdi-alert-circle text-yellow-600'
              "
            ></i>
            <span v-if="image.reviewScore" class="text-gray-500">
              {{ image.reviewScore }}/10
            </span>
          </span>
        </div>
        <div class="p-2">
          <!-- El prompt es contexto secundario: una línea con el texto
               completo en el title, para que mande la imagen. -->
          <p class="ai-images-prompt" :title="image.prompt">
            {{ image.prompt }}
          </p>
          <div class="flex gap-1">
            <button
              class="flex-1 text-xs border rounded px-2 py-1 hover:bg-gray-100 disabled:opacity-50"
              :disabled="busyId === image.id"
              :title="$t('message.product_images.add_to_images')"
              @click="$emit('use-image', image)"
            >
              <i class="mdi mdi-image-plus mr-1"></i>
              {{ $t("message.product_images.add_to_images") }}
            </button>
            <button
              class="flex-1 text-xs border rounded px-2 py-1 hover:bg-gray-100"
              :title="$t('message.product_images.add_to_description')"
              @click="$emit('use-in-description', image)"
            >
              <i class="mdi mdi-text-box-plus-outline mr-1"></i>
              {{ $t("message.product_images.add_to_description") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import { getImageGenerations } from "@/api/store/AiService.js";

export default {
  name: "ProductAiImages",
  props: {
    code: {
      type: String,
      required: true
    },
    // Id de la imagen que se está agregando, para deshabilitar su botón.
    busyId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      images: [],
      loading: false
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        // El historial ya filtra por nkr_code: son las generaciones hechas
        // desde el generador con este producto seleccionado.
        const data = await getImageGenerations({
          nkrCode: this.code,
          limit: 20
        });
        // El panel muestra imágenes, no generaciones: se aplanan conservando
        // el prompt y el veredicto de la generación que las produjo.
        this.images = (data.generations || []).reduce(
          (all, generation) =>
            all.concat(
              (generation.images || []).map(image => ({
                ...image,
                prompt: generation.prompt,
                reviewStatus: generation.reviewStatus,
                reviewScore: generation.reviewScore
              }))
            ),
          []
        );
        this.$emit("loaded", this.images);
      } catch (e) {
        this.$toast.open({
          message: this.$t("message.product_images.ai_images_error"),
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    },
    openFull(image) {
      window.open(image.url, "_blank");
    }
  }
};
</script>

<style scoped>
.ai-images-list {
  /* Grid de tarjetas de ancho fijo: en un panel angosto queda una columna,
     en uno ancho entran varias por fila sin estirar cada foto. */
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  gap: 0.75rem;
  /* Scroll propio: el panel no debe estirar el alto del modal del producto. */
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 0.5rem;
}
.ai-images-thumb {
  /* La imagen manda: marco alto y uniforme, sin recortar el contenido. */
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 15rem;
  background: #fff;
  cursor: zoom-in;
}
.ai-images-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.ai-images-badge {
  position: absolute;
  top: 0.25rem;
  left: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0 0.25rem;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.9);
  font-size: 0.7rem;
  line-height: 1.2rem;
}
.ai-images-prompt {
  /* Contexto secundario: una sola línea, el prompt completo va en el title. */
  margin-bottom: 0.5rem;
  color: #9ca3af;
  font-size: 0.7rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
